/* ========================================================================== */
/* File: src/components/layout/Sidebar.js (Updated)             */
/* ========================================================================== */
import React, { useState, useEffect, useCallback } from 'react';
import { LayoutDashboard, Building, Brush, Users, Settings, BarChart2, DollarSign, EarthIcon, HeartPlusIcon, Microscope, Share2 } from 'lucide-react';
import companyLogo from '../../assets/logo.png';
import { api } from '../../api';

const NavLink = ({ view, activeView, icon, children, onClick }) => (
    <a
      href="#"
      onClick={(e) => { e.preventDefault(); onClick(view); }}
      className={`nav-link ${activeView === view ? 'active' : ''}`}
    >
      {React.cloneElement(icon, { size: 20 })}
      <span>{children}</span>
    </a>
);

const Sidebar = ({ user, activeView, setActiveView, isOpen }) => {
    const [permissions, setPermissions] = useState({});
    const [selectedPropertyId, setSelectedPropertyId] = useState(null);
    const [permissionsLoaded, setPermissionsLoaded] = useState(false);

    // Read selected property from localStorage
    useEffect(() => {
        const storedPropertyId = localStorage.getItem('selectedPropertyId');
        if (storedPropertyId) {
            setSelectedPropertyId(parseInt(storedPropertyId));
        }

        // Listen for storage changes
        const handleStorageChange = () => {
            const newPropertyId = localStorage.getItem('selectedPropertyId');
            setSelectedPropertyId(newPropertyId ? parseInt(newPropertyId) : null);
        };
        
        window.addEventListener('storage', handleStorageChange);
        
        // Also poll for changes (since localStorage doesn't fire events in same tab)
        const interval = setInterval(() => {
            const currentId = localStorage.getItem('selectedPropertyId');
            const currentIdNum = currentId ? parseInt(currentId) : null;
            setSelectedPropertyId(prev => {
                if (prev !== currentIdNum) {
                    return currentIdNum;
                }
                return prev;
            });
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(interval);
        };
    }, []);

    // Fetch permissions when property changes
    useEffect(() => {
        const fetchPermissions = async () => {
            // SuperAdmin always sees everything
            if (!user || user.role === 'SuperAdmin') {
                setPermissions({});
                setPermissionsLoaded(true);
                return;
            }

            if (!selectedPropertyId) {
                setPermissions({});
                setPermissionsLoaded(true);
                return;
            }

            try {
                setPermissionsLoaded(false);
                console.log('Fetching permissions for property:', selectedPropertyId, 'role:', user.role);
                const data = await api.getPropertyPermissions(selectedPropertyId);
                console.log('Permissions data:', data);
                const permObj = {};
                data.forEach(p => {
                    if (!permObj[p.role]) permObj[p.role] = {};
                    permObj[p.role][p.feature_key] = !!p.is_visible;
                });
                console.log('Permissions object:', permObj);
                setPermissions(permObj);
                setPermissionsLoaded(true);
            } catch (error) {
                console.error('Failed to load permissions:', error);
                setPermissions({});
                setPermissionsLoaded(true);
            }
        };

        fetchPermissions();
    }, [user, selectedPropertyId]);

    const hasPermission = useCallback((featureKey) => {
        // SuperAdmin always has access
        if (!user || user.role === 'SuperAdmin') {
            return true;
        }

        // If no property selected, hide tabs (user needs to select property first)
        if (!selectedPropertyId) {
            return false;
        }

        // If permissions not loaded yet, hide tabs (will update when loaded)
        if (!permissionsLoaded) {
            return false;
        }

        // If permissions loaded but role not found, hide tabs (safe default)
        if (!permissions[user.role]) {
            console.log(`No permissions found for role: ${user.role}`);
            return false;
        }

        // Check permission - only show if explicitly true
        const hasAccess = permissions[user.role][featureKey] === true;
        console.log(`Permission check: ${user.role}.${featureKey} = ${hasAccess}`);
        return hasAccess;
    }, [user, selectedPropertyId, permissions, permissionsLoaded]);

    return (
        <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
            <div className="sidebar-logo">
                <img src={companyLogo} alt="Your Company Logo"/>
            </div>
            <nav className="sidebar-nav">

                {/* --- TOP NAVIGATION GROUP --- */}
                <div className="nav-group-top">
                    <NavLink view="dashboard" activeView={activeView} onClick={setActiveView} icon={<LayoutDashboard />} >Dashboard</NavLink>
                    <NavLink view="properties" activeView={activeView} onClick={setActiveView} icon={<Building />} >Properties</NavLink>
                    {hasPermission('reports') && (
                        <NavLink view="reports" activeView={activeView} onClick={setActiveView} icon={<BarChart2 />} >Reports</NavLink>
                    )}
                    {hasPermission('housekeeping') && (
                        <NavLink view="Housekeeping" activeView={activeView} onClick={setActiveView} icon={<Brush />} >Housekeeping</NavLink>
                    )}
                    {user && user.role === 'SuperAdmin' && (
                        <NavLink view="eventLogs" activeView={activeView} onClick={setActiveView} icon={<Microscope />} >Event Logs</NavLink>
                    )}
                    {hasPermission('billing') && (
                        <NavLink view="billing" activeView={activeView} onClick={setActiveView} icon={<DollarSign />} >Billing</NavLink>
                    )}
                    {hasPermission('domains') && (
                        <NavLink view="domains" activeView={activeView} onClick={setActiveView} icon={<EarthIcon />} >Domains</NavLink>
                    )}
                    {hasPermission('social') && (
                        <NavLink view="social" activeView={activeView} onClick={setActiveView} icon={<Share2 />} >Social</NavLink>
                    )}                   
                </div>

                {/* --- BOTTOM NAVIGATION GROUP --- */}
                <div className="nav-group-bottom">
                    {hasPermission('support') && (
                        <NavLink view="support" activeView={activeView} onClick={setActiveView} icon={<HeartPlusIcon />} >Support</NavLink>
                    )}
                    <NavLink view="settings" activeView={activeView} onClick={setActiveView} icon={<Settings />} >Settings</NavLink>
                </div>
                
                
            </nav>
        </aside>
    );
};

export default Sidebar;